import { InvestorReportDTO, InvestorReportTemplateDTO } from '@types';
import { DateTime } from 'luxon';
import { TableSort } from '@/types';
import { ReportStatuses, ReportsFrequencies } from '@shared/constants';

export const getStatusColor = (report: InvestorReportDTO) => {
  switch (report.status) {
    case ReportStatuses.open:
      return 'yellow';
    case ReportStatuses.partial:
      return 'yellow';
    case ReportStatuses.completed:
      return 'green';
    case ReportStatuses.closed:
      return 'green';
    case ReportStatuses.overdue:
      return 'red';
    case ReportStatuses.deleted:
      return 'gray';
    default:
      return 'gray';
  }
};

export const getCardTitleColor = (status: ReportStatuses) => {
  switch (status) {
    case ReportStatuses.open:
      return 'error';
    case ReportStatuses.completed:
      return 'accent';
    case ReportStatuses.closed:
      return 'accent';
    case ReportStatuses.overdue:
      return 'error';
    case ReportStatuses.deleted:
      return 'error';
    default:
      return 'error';
  }
};

export const formatDate = (date?: string) => {
  if (date) {
    return DateTime.fromISO(date).toFormat('MM/dd/yyyy');
  }
  return ' - ';
};

export interface InvestorListProps {
  investorReports: InvestorReportDTO[];
  recordsPerPage: number;
  selectedPage: number;
  onPageSelect: (page: number) => void;
  onSelectRowsChange: (page: number) => void;
  searchText: string;
  investorReportTemplates: InvestorReportTemplateDTO[];
}

export interface InvestorReportingTableData extends InvestorReportDTO {
  company_name: string;
  investor_name: string;
  last_investor_update: string;
  next_request_date: string;
  firm_name: string;
}

export interface InvestorReportsDetailsData {
  investor_name: string;
  investor_reports: InvestorReportDTO[];
  selected_investor_report?: InvestorReportDTO;
}

export interface ReportsGroupedData {
  key: string;
  client_id: number;
  firm_id?: number;
  user_id?: number;
  company_name: string;
  investor_name: string;
  investorReports: InvestorReportDTO[];
  investorReportTemplates: InvestorReportTemplateDTO[];
}

export interface InvestorReportingGroupedTableData extends ReportsGroupedData {
  last_investor_update: string;
  next_request_date: string;
  overdueReports: InvestorReportDTO[];
  due_date?: string;
}

export const generateReportsTableData = (
  investorReports: InvestorReportDTO[],
  investorReportTemplates: InvestorReportTemplateDTO[]
) => {
  const records: ReportsGroupedData[] = [];
  investorReports.forEach((report) => {
    const key = `${report.client_id}_${report.firm_id || ''}_${
      report.user_id || ''
    }`;
    const reportTableData = records.find((r) => r.key === key);
    if (!reportTableData) {
      records.push({
        key,
        client_id: report.client_id,
        firm_id: report.firm_id,
        user_id: report.user_id,
        company_name: report.client?.client_name || '',
        investor_name: report.firm_id
          ? report.firm?.firm_name || ''
          : `${report.user?.first_name as string} ${
              report.user?.last_name as string
            }`,
        investorReports: [report],
        investorReportTemplates: [],
      });
    } else {
      reportTableData.investorReports.push(report);
    }
  });
  investorReportTemplates.forEach((template) => {
    const key = `${template.client_id}_${template.firm_id || ''}_${
      template.user_id || ''
    }`;
    const reportTableData = records.find((r) => r.key === key);
    if (!reportTableData) {
      records.push({
        key,
        client_id: template.client_id,
        firm_id: template.firm_id,
        user_id: template.user_id,
        company_name: template.client?.client_name || '',
        investor_name: template.firm_id
          ? template.firm?.firm_name || ''
          : `${template.user?.first_name as string} ${
              template.user?.last_name as string
            }`,
        investorReports: [],
        investorReportTemplates: [template],
      });
    } else {
      reportTableData.investorReportTemplates.push(template);
    }
  });
  return records;
};

export const findTemplateForReport = (
  investorReportTemplates: InvestorReportTemplateDTO[],
  report: InvestorReportDTO
) => {
  if (!investorReportTemplates || !report) return undefined;
  return investorReportTemplates.find(
    (template) =>
      template.frequency === report?.frequency &&
      template.client_id === report?.client_id &&
      template.firm_id === report?.firm_id &&
      template.user_id === report?.user_id
  );
};

export const resolveNextRequestDate = (
  report: InvestorReportDTO,
  template: InvestorReportTemplateDTO | undefined
) => {
  const now = DateTime.now();
  const date = DateTime.fromISO(report.created_at as unknown as string);
  switch (report.frequency) {
    case ReportsFrequencies.monthly: {
      if (!template) return ' - ';

      const startingDay = template.starting_date || 1;
      const currentScheduledDate = DateTime.local(
        now.year,
        now.month,
        startingDay
      );
      const createdAt = DateTime.fromISO(report.created_at);
      const MaxDifferenceDays = 7;

      // If report was created after the current scheduled date, move to the next month
      if (createdAt >= currentScheduledDate) {
        return currentScheduledDate.plus({ months: 1 }).toFormat('MM/dd/yyyy');
      }

      // If the scheduled date has not occurred yet this month, use it directly
      if (currentScheduledDate >= now) {
        return currentScheduledDate.toFormat('MM/dd/yyyy');
      }

      // If the scheduled date has passed but was less than 7 days ago, use the current date
      const daysSinceScheduled = now.diff(currentScheduledDate, 'days').days;
      if (daysSinceScheduled < MaxDifferenceDays && daysSinceScheduled >= 0) {
        return now.toFormat('MM/dd/yyyy');
      }

      // Any other case, move the scheduled date to the next month
      return currentScheduledDate.plus({ months: 1 }).toFormat('MM/dd/yyyy');
    }

    case ReportsFrequencies.quarterly: {
      const currentQuarter = Math.ceil(date.month / 3);

      const currentQuarterStart = DateTime.fromObject({
        year: date.year,
        month: (currentQuarter - 1) * 3 + 1,
        day: 1,
      }).startOf('day');

      const nextQuarterStart = currentQuarterStart.plus({ months: 3 });
      return nextQuarterStart.toFormat('MM/dd/yyyy');
    }

    case ReportsFrequencies.annual: {
      const currentYearStart = DateTime.fromObject({
        year: date.year,
        month: 1,
        day: 1,
      }).startOf('day');

      const nextYearStart = currentYearStart.plus({ years: 1 });
      return nextYearStart.toFormat('MM/dd/yyyy');
    }

    case ReportsFrequencies.oneTime:
      return ' - ';

    default:
      return ' - ';
  }
};

export const generateURLForReportDetails = (
  clientNameFromQuery: string,
  client_id?: number,
  firm_id?: number,
  user_id?: number,
  report_id?: number,
  pendingReportId?: number,
  showPastUpdates?: boolean
) => {
  const baseURL = `/i-reporting-details`;
  const clientName = encodeURIComponent(clientNameFromQuery);
  const clientId = client_id ? encodeURIComponent(client_id) : '';
  const firmId = firm_id ? encodeURIComponent(firm_id) : '';
  const userId = user_id ? encodeURIComponent(user_id) : '';
  const reportId = report_id ? encodeURIComponent(report_id) : '';

  let url = baseURL;
  url += clientNameFromQuery
    ? `?clientName=${clientName}&clientId=${clientId}`
    : `?clientId=${clientId}`;
  url += `&firmId=${firmId}&userId=${userId}&reportId=${reportId}`;
  url += pendingReportId ? `&pendingReportId=${pendingReportId}` : '';
  url += clientNameFromQuery || showPastUpdates ? `&showPastUpdates=true` : '';
  return url;
};

export const frequencies = [
  { index: 0, label: ReportsFrequencies.monthly },
  { index: 1, label: ReportsFrequencies.quarterly },
  { index: 2, label: ReportsFrequencies.annual },
  { index: 3, label: ReportsFrequencies.oneTime },
];

export const mapApiInvestorReportsDataToTableData = (
  values: InvestorReportDTO[],
  investorReportTemplates: InvestorReportTemplateDTO[]
) => {
  return values.map((value) => {
    const template = findTemplateForReport(investorReportTemplates, value);
    const lastInvestorUpdate = DateTime.fromISO(
      value.created_at?.toString() || ''
    );
    return {
      ...value,
      id: value.id,
      company_name: value.client?.client_name || '',
      investor_name: value.firm
        ? value.firm.firm_name
        : `${value.user?.first_name as string} ${
            value.user?.last_name as string
          }`,
      last_investor_update: lastInvestorUpdate.toFormat('MM/dd/yyyy') || 'N/A ',
      next_request_date: resolveNextRequestDate(value, template),
      firm_name: value.firm?.firm_name || '',
      period: value.period,
    };
  });
};

const getSortValueForPastUpdates = (
  sortField: string,
  report: InvestorReportingTableData
) => {
  switch (sortField) {
    case 'last_investor_update':
      return report?.last_investor_update
        ? new Date(report?.last_investor_update).getTime()
        : 0;
    case 'next_request_date':
      return report?.next_request_date
        ? new Date(report?.next_request_date).getTime()
        : 0;
    default:
      return report[sortField as keyof InvestorReportingTableData];
  }
};

export const sortForPastUpdates = (
  sort: TableSort<InvestorReportingTableData>
) => {
  return (a: InvestorReportingTableData, b: InvestorReportingTableData) => {
    const obj1 = getSortValueForPastUpdates(sort.field, a);
    const obj2 = getSortValueForPastUpdates(sort.field, b);
    if (obj1?.toString() && obj2?.toString() && obj1 > obj2)
      return sort.asc ? 1 : -1;
    return sort.asc ? -1 : 1;
  };
};
